import { useAppSelector } from "store/index";
import { selectWorksheetIdByPage } from "store/selectors";
import {
  OrgCapability,
  SectionCapability,
  selectOrgCapabilities,
  selectSectionCapabilities,
  selectSectionCapabilitiesBySectionId,
  selectSectionCapabilitiesByUserId,
  selectSectionUserIdsWithSectionCapability,
  selectUserIdsWhoCanAuthorSection,
  selectUserIdsWhoCanParticipateInSection,
  selectUserPageCapabilities,
} from "store/selectors/permissions";
import { useListSelector } from "store/store-hooks";

/**
 * Hook to get all section-level capabilities for the current user in the current section and
 * all org-level capabilities for the current user in the current org.
 *
 * @returns An object containing all capabilities
 *
 * @example
 * ```tsx
 * const capabilities = useCapabilities();
 * // Returns: { canAuthorOrg: false, canAuthorSection: true, ... }
 * ```
 */
export const useCapabilities = () => {
  const sectionCapabilities = useAppSelector(selectSectionCapabilities);
  const orgCapabilities = useAppSelector(selectOrgCapabilities);
  return { ...sectionCapabilities, ...orgCapabilities } as Record<
    SectionCapability | OrgCapability,
    boolean
  >;
};

/**
 * Hook to get all capabilities for a specific user in the current section.
 *
 * @param userId - The ID of the user to get capabilities for
 * @returns An object containing the user's capabilities
 *
 * @example
 * ```tsx
 * const userCapabilities = useSectionCapabilitiesByUserId('user123');
 * // Returns: { canAuthorSection: false, canParticipateInSection: true, ... }
 * ```
 */
export const useSectionCapabilitiesByUserId = (userId: string) => {
  // Note: currently, this only contains section capabilities. If needed, we can add org capabilities here
  // similarly to useCapabilities.
  return useAppSelector((state) => selectSectionCapabilitiesByUserId(state, userId));
};

/**
 * Hook to get a list of section user IDs that have a specific capability.
 *
 * @param capability - The capability to filter section users by
 * @returns An array of section user IDs that have the specified capability
 */
export const useSectionUserIdsWithSectionCapability = (capability: SectionCapability) => {
  return useListSelector((state) => selectSectionUserIdsWithSectionCapability(state, capability));
};

/**
 * Hook to get all section-level capabilities for the current user in a specific section.
 *
 * @param sectionId - The ID of the section to get capabilities for
 * @returns An object containing the section's capabilities
 *
 * @example
 * ```tsx
 * const sectionCapabilities = useSectionCapabilitiesBySectionId('section456');
 * // Returns: { canAuthorSection: false, canParticipateInSection: true, ... }
 * ```
 */
export const useSectionCapabilitiesBySectionId = (sectionId: string) => {
  return useAppSelector((state) => selectSectionCapabilitiesBySectionId(state, sectionId));
};

/**
 * Hook to get a list of user IDs for the users that can participate in the current section.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanParticipateInSection = () => {
  return useListSelector(selectUserIdsWhoCanParticipateInSection);
};

/**
 * Hook to get a list of user IDs for the users that can author the current section.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanAuthorSection = () => {
  return useListSelector(selectUserIdsWhoCanAuthorSection);
};

/**
 * Hook to get all page-level capabilities for the current user on a specific page.
 *
 * @param pageId - The ID of the page to get capabilities for
 * @returns An object containing the page's capabilities
 */
export const usePageCapabilitiesForPageId = (pageId: string) => {
  const worksheetId = useAppSelector((s) => selectWorksheetIdByPage(s, pageId));

  return useAppSelector((state) => selectUserPageCapabilities(state, pageId, worksheetId));
};
